import { atom, type WritableAtom } from 'nanostores';
import { userStore } from '~/lib/stores/user';
import type { UserData } from '~/services/interfaceService';
import { db, getAll } from '~/lib/persistence';
import type { ChatHistoryItem as OriginalChatHistoryItem } from '~/services/interfaceService';
import {
  Free_Git_Repo_Limit,
  Pro_Git_Repo_Limit,
  Free_Total_Repos_Limit,
  Pro_Total_Repos_Limit,
  Index_DB_quota,
} from '~/utils/constants';
import type { CloudChatHistoryItem } from '~/services/interfaceService';

export interface ExtendedChatHistoryItem extends OriginalChatHistoryItem {
  initSource?: string;
}

export interface StorageStatusState {
  isLoading: boolean;
  error: Error | null;
  allProjects: CloudChatHistoryItem[];
  githubProjects: CloudChatHistoryItem[];
  otherProjects: CloudChatHistoryItem[];
  githubRepoUsagePercent: number;
  totalRepoUsagePercent: number;
  showStorageWarning: boolean;
  githubRepoLimit: number;
  totalRepoLimit: number;
  currentGithubRepoCount: number;
  currentTotalRepoCount: number;
  indexDBUsagePercent: number;
  indexDBUsageMB: number;
}

const initialStorageStatusState: StorageStatusState = {
  isLoading: true,
  error: null,
  allProjects: [],
  githubProjects: [],
  otherProjects: [],
  githubRepoUsagePercent: 0,
  totalRepoUsagePercent: 0,
  showStorageWarning: false,
  githubRepoLimit: Free_Git_Repo_Limit,
  totalRepoLimit: Free_Total_Repos_Limit,
  currentGithubRepoCount: 0,
  currentTotalRepoCount: 0,
  indexDBUsagePercent: 0,
  indexDBUsageMB: 0,
};

export const storageStatusStore: WritableAtom<StorageStatusState> = atom(initialStorageStatusState);

/**
 * Updates derived state (counts, percentages, limits) based on all projects and user data.
 */
async function updateDerivedStorageState(projects: CloudChatHistoryItem[], userData: UserData) {
  const currentState = storageStatusStore.get();
  const isPro = userData.isSubscribed;
  const currentGitHubLimit = isPro ? Pro_Git_Repo_Limit : Free_Git_Repo_Limit;
  const currentTotalLimit = isPro ? Pro_Total_Repos_Limit : Free_Total_Repos_Limit;
  const currentIndexDBLimit = Index_DB_quota;
  const ghProjects: CloudChatHistoryItem[] = [];
  const othProjects: CloudChatHistoryItem[] = [];

  const finalProjects: CloudChatHistoryItem[] = [];

  for (const project of projects) {
    // map idb projects with the cloud projects
    if (project.type === 'local') {
      if (db) {
        const idbProjects = await getAll(db);
        const matchingProject = idbProjects.find((idbProject) => idbProject.projectId === project.projectId);

        if (matchingProject) {
          finalProjects.push(project);
        }
      }
    } else if (project.type === 'private') {
      finalProjects.push(project);
    }

    if (project.projectName) {
      if (project.initSource === 'github') {
        ghProjects.push(project);
      } else {
        othProjects.push(project);
      }
    }
  }

  const numGithubRepos = ghProjects.length;
  const numTotalRepos = projects.filter((p) => p && p.projectName).length;
  const indexDBUsage = await navigator.storage.estimate();
  const indexDBUsageMB = Math.round((indexDBUsage?.usage ? indexDBUsage.usage / 1024 / 1024 : 0) * 100) / 100;

  const ghUsage = currentGitHubLimit > 0 ? (numGithubRepos / currentGitHubLimit) * 100 : numGithubRepos > 0 ? 100 : 0;
  const totalUsage = currentTotalLimit > 0 ? (numTotalRepos / currentTotalLimit) * 100 : numTotalRepos > 0 ? 100 : 0;
  const indexDBUsagePercent = Math.round((indexDBUsageMB / currentIndexDBLimit) * 100);
  console.log('[storageStatusStore] IndexDB usage:', indexDBUsageMB, 'MB', indexDBUsagePercent, '%');

  console.log('[finalProjects] Final projects:', finalProjects);

  storageStatusStore.set({
    ...currentState,
    allProjects: finalProjects, // Ensure allProjects is also updated here if it's the source
    githubProjects: ghProjects,
    otherProjects: othProjects,
    githubRepoLimit: currentGitHubLimit,
    totalRepoLimit: currentTotalLimit,
    currentGithubRepoCount: numGithubRepos,
    currentTotalRepoCount: numTotalRepos,
    githubRepoUsagePercent: ghUsage,
    totalRepoUsagePercent: totalUsage,
    showStorageWarning: ghUsage >= 90 || totalUsage >= 90 || indexDBUsagePercent >= 90,
    isLoading: false,
    error: null,
    indexDBUsagePercent,
    indexDBUsageMB,
  });
}

/**
 * Fetches all projects from the database and processes them to update the storage status.
 */
export async function fetchAndProcessProjects(cloudProjects: CloudChatHistoryItem[]) {
  const currentStoreState = storageStatusStore.get();
  storageStatusStore.set({ ...currentStoreState, isLoading: true, error: null });
  console.log('[upload] Fetching projects...:', cloudProjects);

  const userData = userStore.get();

  /*
   * if (!db) {
   *   console.warn('[upload] IndexedDB not available.');
   *   storageStatusStore.set({
   *     ...initialStorageStatusState, // Reset to initial state but reflect DB error
   *     isLoading: false,
   *     error: new Error('IndexedDB not available'),
   *   });
   *   return;
   * }
   */

  try {
    /*
     * const projectsFromDB = (await getAll(db)) as ExtendedChatHistoryItem[];
     *  // Map over cloud projects and produce ExtendedChatHistoryItem, merging with local when linked
     * const localByUrlId = new Map<string, ExtendedChatHistoryItem>();
     * const localById = new Map<string, ExtendedChatHistoryItem>();
     * const localByProjectId = new Map<string, ExtendedChatHistoryItem>();
     * for (const lp of projectsFromDB) {
     *   if (lp?.urlId) {
     *     localByUrlId.set(lp.urlId, lp);
     *   }
     *   if (lp?.id) {
     *     localById.set(lp.id, lp);
     *   }
     *   if (lp?.projectId) {
     *     localByProjectId.set(lp.projectId, lp);
     *   }
     * }
     * const cloudAsExtended: (ExtendedChatHistoryItem | null)[] = (cloudProjects || []).map((cp) => {
     *   const matchedLocalByUrl = cp?.urlId ? localByUrlId.get(cp.urlId) : undefined;
     *   const matchedLocalById = cp?.idbChatId ? localById.get(cp.idbChatId) : undefined;
     *   const matchedLocalByProjectId = cp?.projectId ? localByProjectId.get(cp.projectId) : undefined;
     *   const matchedLocal: ExtendedChatHistoryItem | undefined =
     *     (matchedLocalByUrl ?? matchedLocalById) && matchedLocalByProjectId;
     *   if (!matchedLocal) {
     *     return null;
     *   }
     *   return {
     *     id: matchedLocal.id,
     *     urlId: matchedLocal.urlId,
     *     description: matchedLocal.description,
     *     messages: matchedLocal.messages,
     *     fileActions: matchedLocal.fileActions,
     *     timestamp: matchedLocal.timestamp,
     *     techStack: matchedLocal.techStack,
     *     isGitRepo: matchedLocal.isGitRepo,
     *     connections: matchedLocal.connections ?? undefined,
     *     initSource: matchedLocal.initSource,
     *     netlifyCreds: matchedLocal.netlifyCreds ?? undefined,
     *     projectId: matchedLocal.projectId,
     *     projectRules: matchedLocal.projectRules,
     *   } as ExtendedChatHistoryItem;
     * });
     * const merged: ExtendedChatHistoryItem[] = [...cloudAsExtended.filter((cp) => cp !== null)];
     */

    await updateDerivedStorageState(cloudProjects, userData);
    console.log('[upload] Projects merged (cloud mapped + unmatched locals) and processed.');
  } catch (e) {
    console.error('[upload] Error fetching projects:', e);

    const currentErrorState = storageStatusStore.get();
    storageStatusStore.set({
      ...currentErrorState,
      isLoading: false,
      error: e instanceof Error ? e : new Error('Failed to fetch projects'),
      allProjects: [], // Clear projects on error
      githubProjects: [],
      otherProjects: [],
      currentGithubRepoCount: 0,
      currentTotalRepoCount: 0,
      githubRepoUsagePercent: 0,
      totalRepoUsagePercent: 0,
      showStorageWarning: false,
    });
  }
}

// Listen to userStore changes to update limits if subscription status changes
userStore.subscribe(async (userData) => {
  const { allProjects, isLoading } = storageStatusStore.get();

  if (allProjects.length > 0 || !isLoading) {
    console.log('[storageStatusStore] User subscription changed, re-calculating storage limits and usage.');
    await updateDerivedStorageState(allProjects, userData);
  }
});
